import { http } from "./client";
import type { PageParams } from "@/types/api";
import type {
  DepartmentComplianceRow,
  DocumentAccessLogEntry,
  DocumentBulkResult,
  DocumentCategory,
  DocumentCategoryStatus,
  DocumentCategoryType,
  DocumentDashboardSummary,
  DocumentMyTasks,
  DocumentRecord,
  DocumentStatus,
  DocumentVersion,
  EmployeeComplianceRow,
  EmployeeRequiredDocument,
} from "@/types/domain";

export interface DocumentFilters extends PageParams {
  status?: DocumentStatus | "";
  category_id?: string;
  employee_id?: string;
  search?: string;
}

export interface DocumentCategoryFilters extends PageParams {
  category_type?: DocumentCategoryType | "";
  status?: DocumentCategoryStatus | "";
}

export interface UploadDocumentInput {
  category_id: string;
  document_name: string;
  file: File;
  // Only meaningful for HR/Tenant Admin uploading on an employee's behalf -
  // every other caller's own employee_id is set server-side regardless of
  // what this carries, see DocumentService.Upload.
  employee_id?: string;
  // "YYYY-MM-DD" - only meaningful for expiry-tracked document types
  // (Passport, Visa, Driving License, Medical Certificate, Professional
  // Certifications, Work Permit). Omit for everything else.
  expiry_date?: string;
}

export const documentsApi = {
  // ---- documents ----
  list: (filters: DocumentFilters = {}) => http.getPage<DocumentRecord>("/documents", filters),
  get: (id: string) => http.get<DocumentRecord>(`/documents/${id}`),
  downloadUrl: (id: string) => `/documents/${id}/download`,
  download: (id: string) => http.download(`/documents/${id}/download`),
  versions: (id: string) => http.get<DocumentVersion[]>(`/documents/${id}/versions`),
  auditLog: (id: string, filters: PageParams = {}) => http.getPage<DocumentAccessLogEntry>(`/documents/${id}/audit-log`, filters),

  upload: (input: UploadDocumentInput) => {
    const form = new FormData();
    form.set("category_id", input.category_id);
    form.set("document_name", input.document_name);
    form.set("file", input.file);
    if (input.employee_id) form.set("employee_id", input.employee_id);
    if (input.expiry_date) form.set("expiry_date", input.expiry_date);
    return http.post<DocumentRecord>("/documents", form);
  },

  update: (id: string, values: Partial<{ document_name: string; category_id: string }>) =>
    http.put<DocumentRecord>(`/documents/${id}`, values),

  replaceFile: (id: string, file: File, values: Partial<{ document_name: string; category_id: string }> = {}) => {
    const form = new FormData();
    form.set("file", file);
    if (values.document_name) form.set("document_name", values.document_name);
    if (values.category_id) form.set("category_id", values.category_id);
    return http.put<DocumentRecord>(`/documents/${id}`, form);
  },

  remove: (id: string) => http.delete<void>(`/documents/${id}`),
  verify: (id: string) => http.patch<DocumentRecord>(`/documents/${id}/verify`, {}),
  reject: (id: string, reason: string) => http.patch<DocumentRecord>(`/documents/${id}/reject`, { reason }),
  requestResubmission: (id: string, remarks: string) =>
    http.patch<DocumentRecord>(`/documents/${id}/request-resubmission`, { remarks }),
  archive: (id: string) => http.patch<DocumentRecord>(`/documents/${id}/archive`, {}),
  bulkVerify: (ids: string[]) => http.post<DocumentBulkResult>("/documents/bulk-verify", { ids }),
  bulkReject: (ids: string[], reason: string) => http.post<DocumentBulkResult>("/documents/bulk-reject", { ids, reason }),

  // ---- categories ----
  categories: (filters: DocumentCategoryFilters = {}) => http.getPage<DocumentCategory>("/document-categories", filters),
  createCategory: (values: { name: string; description?: string; category_type?: DocumentCategoryType; status?: DocumentCategoryStatus }) =>
    http.post<DocumentCategory>("/document-categories", values),
  updateCategory: (
    id: string,
    values: Partial<{ name: string; description: string; category_type: DocumentCategoryType; status: DocumentCategoryStatus }>,
  ) => http.put<DocumentCategory>(`/document-categories/${id}`, values),
  deleteCategory: (id: string) => http.delete<void>(`/document-categories/${id}`),

  // ---- dashboard / my tasks / tenant-wide audit ----
  dashboard: () => http.get<DocumentDashboardSummary>("/document-dashboard"),
  myTasks: () => http.get<DocumentMyTasks>("/document-tasks"),
  auditLogs: (filters: PageParams = {}) => http.getPage<DocumentAccessLogEntry>("/documents/audit-logs", filters),
  requiredDocumentsForEmployee: (employeeId: string) =>
    http.get<EmployeeRequiredDocument[]>(`/documents/employees/${employeeId}/required-documents`),

  // ---- reports: JSON for the on-screen table, CSV for "Export" ----
  reportPendingVerification: () => http.get<{ rows: DocumentRecord[]; count: number }>("/documents/reports/pending-verification"),
  reportRejected: () => http.get<{ rows: DocumentRecord[]; count: number }>("/documents/reports/rejected"),
  reportExpiring: (withinDays?: number) =>
    http.get<{ rows: DocumentRecord[]; count: number }>("/documents/reports/expiring", { within_days: withinDays }),
  reportAudit: () => http.get<{ rows: DocumentAccessLogEntry[]; count: number }>("/documents/reports/audit"),
  reportMissing: () => http.get<{ rows: EmployeeRequiredDocument[]; count: number }>("/documents/reports/missing"),
  reportDepartmentCompliance: () => http.get<{ rows: DepartmentComplianceRow[]; count: number }>("/documents/reports/department-compliance"),
  reportEmployeeCompliance: () => http.get<{ rows: EmployeeComplianceRow[]; count: number }>("/documents/reports/employee-compliance"),
  downloadReport: (reportPath: string, query: Record<string, string | number | undefined> = {}) =>
    http.download(`/documents/reports/${reportPath}`, { ...query, format: "csv" }),
};
